/**
 * warmCache.js — Pré-chargement silencieux de toutes les données dans IndexedDB
 *
 * Appelé au démarrage de l'app (dans AuthContext après login).
 * Recharge en background tous les modules depuis le serveur et les stocke
 * dans IndexedDB pour garantir un mode offline complet.
 *
 * Priorités :
 *   1. Données critiques (clients, produits, factures, devis) → immédiatement
 *   2. Données secondaires (livraisons, retours, stock, POS...) → après 3s
 *   3. Données légères (fournisseurs, crédits) → après 6s
 */

import { dbPutMany, dbPut, metaSet, openDB } from '../db/localDB';

const BASE = process.env.REACT_APP_API_URL || 'https://api.easyfacture.easyclick-solution.fr';

function hdrs() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

async function safeFetch(path, timeout = 10000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeout);
  try {
    const r = await fetch(`${BASE}${path}`, { headers: hdrs(), signal: ctrl.signal });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function cacheStore(store, data) {
  if (!data) return;
  const arr = Array.isArray(data) ? data : (data.data || data.rows || []);
  if (!arr.length) return;
  try {
    await dbPutMany(store, arr.map(i => ({ ...i, _synced: true, _cachedAt: Date.now() })));
    console.debug(`[warmCache] ${store}: ${arr.length} enregistrements mis en cache`);
  } catch (e) {
    console.warn(`[warmCache] Erreur cache ${store}:`, e?.message);
  }
}

/* ── Phase 1 — Données critiques ──────────────────────────────────── */
async function cachePhase1() {
  const [products, clients, invoices, quotes, posSales, company] = await Promise.all([
    safeFetch('/products?limit=9999'),
    safeFetch('/clients?limit=500'),
    safeFetch('/invoices?limit=500'),
    safeFetch('/quotes?limit=500'),
    safeFetch('/pos?limit=200'),
    safeFetch('/company'),
  ]);

  await Promise.all([
    cacheStore('products', products),
    cacheStore('clients', clients),
    cacheStore('invoices', invoices),
    cacheStore('quotes', quotes),
    cacheStore('pos_sales', posSales),
    company && metaSet('company', company).catch(() => {}),
  ]);

  await metaSet('lastWarmCache_phase1', new Date().toISOString()).catch(() => {});
  console.debug('[warmCache] Phase 1 terminée');
}

/* ── Phase 2 — Données secondaires ───────────────────────────────── */
async function cachePhase2() {
  const [deliveries, returns, stockOverview, posSalesExtra] = await Promise.all([
    safeFetch('/deliveries?limit=300'),
    safeFetch('/returns?limit=300'),
    safeFetch('/stock/overview'),
    safeFetch('/pos?limit=500'),
  ]);

  await Promise.all([
    cacheStore('deliveries', deliveries),
    cacheStore('returns', returns),
    // stock overview retourne { products, alerts } — on met à jour les produits avec le stock
    stockOverview?.products && cacheStore('products', stockOverview.products),
    posSalesExtra && cacheStore('pos_sales', posSalesExtra),
  ]);

  await metaSet('lastWarmCache_phase2', new Date().toISOString()).catch(() => {});
  console.debug('[warmCache] Phase 2 terminée');
}

/* ── Phase 3 — Données légères ───────────────────────────────────── */
async function cachePhase3() {
  const [suppliers, purchaseOrders, creditBalances] = await Promise.all([
    safeFetch('/suppliers?limit=200'),
    safeFetch('/purchase-orders?limit=200'),
    safeFetch('/client-credits/balances'),
  ]);

  await Promise.all([
    cacheStore('suppliers', suppliers),
    cacheStore('purchase_orders', purchaseOrders),
    // credits: balances est un tableau { client, balance, totalCredit, totalDebit }
    // on stocke les entrées d'historique si disponibles
  ]);

  // Cache credit history for clients with balance
  if (Array.isArray(creditBalances)) {
    const top = creditBalances.slice(0, 20); // top 20 clients
    for (const { client } of top) {
      if (!client?.id) continue;
      const hist = await safeFetch(`/client-credits/client/${client.id}?limit=50`);
      if (hist?.rows?.length) {
        await cacheStore('credits', hist.rows).catch(() => {});
      }
    }
  }

  await metaSet('lastWarmCache_phase3', new Date().toISOString()).catch(() => {});
  console.debug('[warmCache] Phase 3 terminée');
}

/* ── Export principal ─────────────────────────────────────────────── */
let _warming = false;

export async function warmCache({ force = false } = {}) {
  // Éviter les double-chargements
  if (_warming) return;

  // Vérifier si on est en ligne
  if (!navigator.onLine) {
    console.debug('[warmCache] Hors ligne — warm cache ignoré');
    return;
  }

  // Vérifier si un warm cache récent existe déjà (< 5 minutes)
  if (!force) {
    try {
      await openDB();
      const { metaGet } = await import('../db/localDB');
      const last = await metaGet('lastWarmCache_phase1');
      if (last && Date.now() - new Date(last).getTime() < 5 * 60 * 1000) {
        console.debug('[warmCache] Cache récent — skip');
        return;
      }
    } catch {}
  }

  _warming = true;
  console.debug('[warmCache] Démarrage du warm cache...');

  try {
    // Phase 1 : immédiatement
    await cachePhase1();

    // Phase 2 : après 2s (ne pas bloquer l'UI)
    setTimeout(() => cachePhase2().catch(() => {}), 2000);

    // Phase 3 : après 5s
    setTimeout(() => {
      cachePhase3().catch(() => {});
      _warming = false;
    }, 5000);

  } catch (e) {
    console.warn('[warmCache] Erreur:', e?.message);
    _warming = false;
  }
}

/**
 * Warm cache léger — appelé après chaque navigation de page
 * Recharge uniquement les données de la page courante
 */
export async function warmCachePage(page) {
  if (!navigator.onLine) return;

  const PAGE_FETCHES = {
    clients:    () => safeFetch('/clients?limit=500').then(d => cacheStore('clients', d)),
    products:   () => safeFetch('/products?limit=9999').then(d => cacheStore('products', d)),
    invoices:   () => safeFetch('/invoices?limit=500').then(d => cacheStore('invoices', d)),
    quotes:     () => safeFetch('/quotes?limit=500').then(d => cacheStore('quotes', d)),
    deliveries: () => safeFetch('/deliveries?limit=300').then(d => cacheStore('deliveries', d)),
    returns:    () => safeFetch('/returns?limit=300').then(d => cacheStore('returns', d)),
    pos:        () => safeFetch('/pos?limit=500').then(d => cacheStore('pos_sales', d)),
    stock:      () => safeFetch('/stock/overview').then(d => d?.products && cacheStore('products', d.products)),
    suppliers:  () => safeFetch('/suppliers?limit=200').then(d => cacheStore('suppliers', d)),
  };

  const fn = PAGE_FETCHES[page];
  if (fn) fn().catch(() => {});
}